/**
 * Auction Truth snapshot (spec §35). The facts that must be TRUE on a creative
 * - the sale name, dates, public URL, the featured lots and their hip numbers -
 * frozen at generation time so a campaign does not silently depend on mutable
 * auction state.
 *
 * The other half of the file is drift detection. If the sale's dates move after
 * an asset was generated, the tenant must be warned, and critical drift must
 * block publish (spec §35, §55). `detectTruthDrift` is pure: it compares the
 * snapshot the asset was built from against the live truth and returns the
 * fields that changed, each tagged critical or not. control-api decides what to
 * do with a critical drift (warn, refresh locked fields, or refuse to publish);
 * the classification lives here so admin, API and the publish guard agree.
 *
 * This is the product-trust story from the mockup's "Brand Locked Elements"
 * panel: the platform, not the image model, owns these values (spec §2).
 */

import { z } from 'zod';
import { SnapshotImageSchema } from './brand-kit.js';

/** The auction engine's own outcome vocabulary for a lot (lots.status once the
 *  sale has run): sold, no sale, passed, scratched. Uppercased for the creative. */
export const LOT_RESULT_STATUSES = ['SOLD', 'NO_SALE', 'PASSED', 'SCRATCHED'] as const;
export const LotResultStatusSchema = z.enum(LOT_RESULT_STATUSES);
export type LotResultStatus = z.infer<typeof LotResultStatusSchema>;

/** A lot's frozen result (ADR-56 hardening D), from the settlement record.
 *  The hammer price is present only when the campaign discloses prices; buyer
 *  identity is never part of the truth a creative can see. */
export const TruthLotResultSchema = z
  .object({
    status: LotResultStatusSchema,
    hammerCents: z.number().int().nonnegative().optional(),
  })
  .strict();
export type TruthLotResult = z.infer<typeof TruthLotResultSchema>;

export const TruthLotSchema = z
  .object({
    lotId: z.string().min(1),
    hipNumber: z.string().optional(),
    name: z.string().optional(),
    imageAssetIds: z.array(z.string().min(1)),
    /** The lot's REAL primary photograph, frozen as a rendition. Under
     *  REAL_SUBJECT_REQUIRED this is the only thing that may stand for the
     *  horse (ADR-56 cut 7/8). */
    primaryImage: SnapshotImageSchema.optional(),
    result: TruthLotResultSchema.optional(),
  })
  .strict();
export type TruthLot = z.infer<typeof TruthLotSchema>;

/** Sale-level results (ADR-56 hardening D): counts from the engine's lot
 *  statuses (public on the catalog already); the gross only when disclosed. */
export const TruthResultsSchema = z
  .object({
    lotsOffered: z.number().int().nonnegative(),
    lotsSold: z.number().int().nonnegative(),
    grossCents: z.number().int().nonnegative().optional(),
  })
  .strict();
export type TruthResults = z.infer<typeof TruthResultsSchema>;

export const AuctionTruthSnapshotSchema = z
  .object({
    id: z.string().min(1),
    auctionId: z.string().min(1),
    auctionName: z.string().min(1),
    publicUrl: z.string().min(1),
    biddingOpensAt: z.string().datetime({ offset: true }).optional(),
    biddingClosesAt: z.string().datetime({ offset: true }).optional(),
    location: z.string().optional(),
    lotCount: z.number().int().nonnegative().optional(),
    /** A marketing-friendly rendering of the dates ("Sept 18-21"), computed
     *  once at snapshot time so the same string appears on the graphic, the
     *  copy and the schedule. */
    marketingDateRange: z.string().optional(),
    selectedLots: z.array(TruthLotSchema).optional(),
    results: TruthResultsSchema.optional(),
    generatedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type AuctionTruthSnapshot = z.infer<typeof AuctionTruthSnapshotSchema>;

/** The current, live truth to compare a snapshot against. Same fields, no id
 *  or generatedAt (it is not itself a snapshot). */
export type LiveAuctionTruth = Omit<AuctionTruthSnapshot, 'id' | 'generatedAt'>;

// ---------------------------------------------------------------------------
// Drift
// ---------------------------------------------------------------------------

export const TRUTH_DRIFT_FIELDS = [
  'auctionName',
  'publicUrl',
  'biddingOpensAt',
  'biddingClosesAt',
  'location',
  'lotCount',
  'selectedLots',
] as const;
export type TruthDriftField = (typeof TRUTH_DRIFT_FIELDS)[number];

export interface TruthDriftEntry {
  field: TruthDriftField;
  was: string | number | undefined;
  now: string | number | undefined;
  /** Critical drift must block publish (spec §55). Name, URL, dates and the
   *  featured-lot set are truth-critical; location and lot count warn only. */
  critical: boolean;
}

const CRITICAL_FIELDS: ReadonlySet<TruthDriftField> = new Set([
  'auctionName',
  'publicUrl',
  'biddingOpensAt',
  'biddingClosesAt',
  'selectedLots',
]);

function lotFingerprint(lots: readonly TruthLot[] | undefined): string {
  if (!lots) return '';
  // A result flipping after generation (a sale reconciled, a hammer corrected)
  // is critical drift: a "SOLD $85,000" creative must never outlive the fact.
  return lots
    .map((l) => `${l.lotId}:${l.hipNumber ?? ''}:${l.name ?? ''}:${l.result?.status ?? ''}:${l.result?.hammerCents ?? ''}`)
    .sort()
    .join('|');
}

/**
 * Compare the snapshot an asset was generated from against the live truth.
 * Returns one entry per changed field. An empty array means the asset is still
 * faithful and may publish.
 */
export function detectTruthDrift(
  snapshot: AuctionTruthSnapshot,
  live: LiveAuctionTruth,
): TruthDriftEntry[] {
  const out: TruthDriftEntry[] = [];
  const push = (field: TruthDriftField, was: string | number | undefined, now: string | number | undefined) => {
    out.push({ field, was, now, critical: CRITICAL_FIELDS.has(field) });
  };

  if (snapshot.auctionName !== live.auctionName) push('auctionName', snapshot.auctionName, live.auctionName);
  if (snapshot.publicUrl !== live.publicUrl) push('publicUrl', snapshot.publicUrl, live.publicUrl);
  if (snapshot.biddingOpensAt !== live.biddingOpensAt) push('biddingOpensAt', snapshot.biddingOpensAt, live.biddingOpensAt);
  if (snapshot.biddingClosesAt !== live.biddingClosesAt) push('biddingClosesAt', snapshot.biddingClosesAt, live.biddingClosesAt);
  if (snapshot.location !== live.location) push('location', snapshot.location, live.location);
  if (snapshot.lotCount !== live.lotCount) push('lotCount', snapshot.lotCount, live.lotCount);

  const wasLots = lotFingerprint(snapshot.selectedLots);
  const nowLots = lotFingerprint(live.selectedLots);
  if (wasLots !== nowLots) push('selectedLots', wasLots || undefined, nowLots || undefined);

  return out;
}

/** True when any drift is critical - the caller must block publish. */
export function hasCriticalDrift(drift: readonly TruthDriftEntry[]): boolean {
  return drift.some((d) => d.critical);
}
